import {
  type ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  useParams,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  buildPlaygroundConversationDestination,
  buildPlaygroundNewChatDestination,
  buildPlaygroundStandaloneDestination,
  type PlaygroundNavigateOptions,
  type PlaygroundRouting,
  PlaygroundRoutingProvider,
} from "./playground-routing";

export const TanStackPlaygroundRoutingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useRouter();
  const { id } = useParams({ strict: false }) as { id?: string };
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const routeNamespace = "playground" as const;
  const newChatDestination =
    buildPlaygroundNewChatDestination(routeNamespace);

  const navigate = useCallback(
    (destination: string, options?: PlaygroundNavigateOptions) => {
      if (options?.replace) {
        router.history.replace(destination);
        return;
      }
      router.history.push(destination);
    },
    [router.history],
  );
  const buildConversationDestination = useCallback(
    (conversationId: string) =>
      buildPlaygroundConversationDestination(routeNamespace, conversationId),
    [],
  );
  const navigateToNewChat = useCallback(
    (options?: PlaygroundNavigateOptions) => {
      navigate(newChatDestination, options);
    },
    [navigate, newChatDestination],
  );
  const navigateToConversation = useCallback(
    (conversationId: string, options?: PlaygroundNavigateOptions) => {
      navigate(buildConversationDestination(conversationId), options);
    },
    [buildConversationDestination, navigate],
  );

  const value = useMemo<PlaygroundRouting>(
    () => ({
      id,
      pathname,
      routeNamespace,
      navigate,
      newChatDestination,
      buildConversationDestination,
      buildStandaloneDestination: buildPlaygroundStandaloneDestination,
      navigateToNewChat,
      navigateToConversation,
    }),
    [
      buildConversationDestination,
      id,
      navigate,
      navigateToConversation,
      navigateToNewChat,
      newChatDestination,
      pathname,
    ],
  );

  return (
    <PlaygroundRoutingProvider value={value}>
      {children}
    </PlaygroundRoutingProvider>
  );
};
